import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Input, RadioInput } from "@jhool-io/fe-components";

import { useUpdatePayer } from "../../hooks/payers.mutations";
import { IPayer } from "../../types/payers.types";
import useToast from "../../../../hooks/useToast";

interface Props {
    selectedPayer: IPayer;
    onFormSubmit: () => void;
}

export default function EditPayerDetails({
    selectedPayer,
    onFormSubmit,
}: Props) {
    const updatePayer = useUpdatePayer();
    const queryClient = useQueryClient();

    const { toast } = useToast();

    // form validation schema
    const schema = yup.object().shape({
        name: yup.string().required("Provider Name is required"),
        payer_id: yup.string(),
        payer_name: yup.string(),
        payer_address_1: yup.string(),
        payer_address_2: yup.string(),
        payer_city: yup.string(),
        payer_state: yup.string(),
        payer_zipcode: yup.string(),
        payer_enrolled: yup.boolean().default(false),
        attachment_payer_id: yup.string(),
        alias: yup.string(),
        attachment_payer_name: yup.string(),
    });

    const {
        register,
        formState: { errors },
        handleSubmit,
        control,
        setValue,
    } = useForm<IPayer>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    // auto fill edit form details
    React.useEffect(() => {
        setValue("insurance_provider_id", selectedPayer.insurance_provider_id);

        setValue("name", selectedPayer.name || "");
        setValue("payer_id", selectedPayer.payer_id || "");
        setValue("payer_name", selectedPayer.payer_name || "");
        setValue("payer_address_1", selectedPayer.payer_address_1 || "");
        setValue("payer_address_2", selectedPayer.payer_address_2 || "");
        setValue("payer_city", selectedPayer.payer_city || "");
        setValue("payer_state", selectedPayer.payer_state || "");
        setValue("payer_zipcode", selectedPayer.payer_zipcode || "");
        setValue("payer_enrolled", selectedPayer.payer_enrolled);
        setValue(
            "attachment_payer_id",
            selectedPayer.attachment_payer_id || ""
        );
        setValue("alias", selectedPayer.alias || "");
        setValue(
            "attachment_payer_name",
            selectedPayer.attachment_payer_name || ""
        );
    }, [selectedPayer, setValue]);

    // edit payer submit function
    const onSubmit = (data: IPayer) => {
        updatePayer.mutate(data, {
            onSuccess(res) {
                queryClient.invalidateQueries({ queryKey: ["payers"] });
                toast({
                    mode: "success",
                    message: res.message || "Payer edited successfully",
                });

                onFormSubmit();
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update payer details. Please try again",
                });
            },
        });
    };

    return (
        <form id="edit-payer-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Insurance Provider Name"
                    {...register("name")}
                    hasError={!!errors.name}
                    errorText={errors.name?.message}
                />

                <Input
                    label="Payer's ID"
                    {...register("payer_id")}
                    hasError={!!errors.payer_id}
                    errorText={errors.payer_id?.message}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Payer's Name"
                    {...register("payer_name")}
                    hasError={!!errors.payer_name}
                    errorText={errors.payer_name?.message}
                />

                <Input
                    label="Payer's Short Code"
                    {...register("alias")}
                    hasError={!!errors.alias}
                    errorText={errors.alias?.message}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Payer's Address 1"
                    {...register("payer_address_1")}
                    hasError={!!errors.payer_address_1}
                    errorText={errors.payer_address_1?.message}
                />

                <Input
                    label="Payer's Address 2"
                    {...register("payer_address_2")}
                    hasError={!!errors.payer_address_2}
                    errorText={errors.payer_address_2?.message}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Payer's City"
                    {...register("payer_city")}
                    hasError={!!errors.payer_city}
                    errorText={errors.payer_city?.message}
                />

                <Input
                    label="Payer's State"
                    {...register("payer_state")}
                    hasError={!!errors.payer_state}
                    errorText={errors.payer_state?.message}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Payer's ZipCode"
                    {...register("payer_zipcode")}
                    hasError={!!errors.payer_zipcode}
                    errorText={errors.payer_zipcode?.message}
                />

                <Input
                    label="Attachment Payer's ID"
                    {...register("attachment_payer_id")}
                    hasError={!!errors.attachment_payer_id}
                    errorText={errors.attachment_payer_id?.message}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Attachment Payer's Name"
                    {...register("attachment_payer_name")}
                    hasError={!!errors.attachment_payer_name}
                    errorText={errors.attachment_payer_name?.message}
                    defaultValue={selectedPayer.attachment_payer_name}
                />

                <div>
                    <p className="font-medium !text-caption text-textSec flex items-center gap-x-1 mb-1">
                        Has Payer Enrolled
                    </p>
                    <div className="flex gap-12 items-center">
                        <Controller
                            control={control}
                            name="payer_enrolled"
                            render={({ field }) => (
                                <RadioInput
                                    label="Yes"
                                    checked={field.value}
                                    onChange={() =>
                                        setValue("payer_enrolled", true)
                                    }
                                    wrapperClass="w-[70px]"
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            name="payer_enrolled"
                            render={({ field }) => (
                                <RadioInput
                                    label="No"
                                    checked={!field.value}
                                    onChange={() =>
                                        setValue("payer_enrolled", false)
                                    }
                                    wrapperClass="w-[70px]"
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </form>
    );
}
